import React from "react";
import { adminApi } from "../lib/api";
import { Mail, Download, Trash2, RefreshCw } from "lucide-react";

export default function NewsletterPanel() {
  const [data, setData] = React.useState({ subscribers: [], total: 0, active_total: 0 });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi().get("/admin/newsletter-subscribers");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const unsubscribe = async (id) => {
    if (!window.confirm("Afmeld denne abonnent?")) return;
    try {
      await adminApi().delete(`/admin/newsletter-subscribers/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke afmelde");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await adminApi().get("/admin/newsletter-subscribers/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `newsletter_subscribers_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Kunne ikke hente CSV: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-5" data-testid="newsletter-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-dark flex items-center gap-2">
            <Mail size={20} className="text-brand-red" />
            Nyhedsbrev — abonnenter
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            <strong>{data.active_total}</strong> aktive · {data.total} i alt (inkl. afmeldte)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="newsletter-refresh"
          >
            <RefreshCw size={14} /> Opdater
          </button>
          <button
            onClick={exportCsv}
            disabled={data.active_total === 0}
            className="rounded-lg bg-brand-dark text-white px-3 py-2 text-sm hover:bg-brand-dark/90 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="newsletter-export"
          >
            <Download size={14} /> Eksportér CSV
          </button>
        </div>
      </div>

      {loading && <div className="text-brand-text2">Indlæser...</div>}
      {error && <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-red-800 text-sm">Fejl: {error}</div>}

      {!loading && data.subscribers.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center" data-testid="newsletter-empty">
          <Mail size={32} className="mx-auto text-slate-400 mb-2" />
          <p className="text-brand-text2">Ingen abonnenter endnu. Del blog-artiklerne for at få første tilmeldinger.</p>
        </div>
      )}

      {data.subscribers.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="newsletter-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-3 px-4">Email</th>
                <th className="py-3 px-4">Kilde</th>
                <th className="py-3 px-4">Tilmeldt</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {data.subscribers.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-b-0" data-testid={`newsletter-row-${s.id}`}>
                  <td className="py-2.5 px-4 font-semibold text-brand-dark">{s.email}</td>
                  <td className="py-2.5 px-4 text-brand-text2">{s.source || "—"}</td>
                  <td className="py-2.5 px-4 text-brand-text2 text-xs">{(s.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="py-2.5 px-4">
                    {s.active === false ? (
                      <span className="inline-block rounded-md bg-slate-100 text-slate-600 px-2 py-0.5 text-xs">Afmeldt</span>
                    ) : (
                      <span className="inline-block rounded-md bg-green-100 text-green-800 px-2 py-0.5 text-xs">Aktiv</span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    {s.active !== false && (
                      <button
                        onClick={() => unsubscribe(s.id)}
                        className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-xs"
                        data-testid={`newsletter-unsub-${s.id}`}
                      >
                        <Trash2 size={12} /> Afmeld
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
